import { useEffect, useState } from 'react';

import {
  getCourseraPlusUpsellEligibilityData,
  isEligibleForCourseraPlusUpgrade,
  isFreemiumPricingEnabled,
} from 'bundles/coursera-plus-common/utils/courseraPlusUpgradeUtils';
import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';

type UseIsEligibleForCourseraPlusUpsellProps = {
  s12nId: string | undefined;
  courseId: string | undefined;
  skip?: boolean;
};

export const useIsEligibleForCourseraPlusUpsell = ({
  s12nId,
  courseId,
  skip = false,
}: UseIsEligibleForCourseraPlusUpsellProps): boolean => {
  const [isEligible, setIsEligible] = useState(false);
  const productItemId = s12nId || courseId;
  const productType = s12nId ? SPECIALIZATION : VERIFIED_CERTIFICATE;

  useEffect(() => {
    if (skip || !productItemId || !productType) {
      setIsEligible(false);
      return;
    }

    const checkEligibility = async () => {
      try {
        if (isFreemiumPricingEnabled()) {
          // For Freemium pricing experiment, we use the new API for upsell eligibility checks
          // If not eligible, eligibilityData from the BE will be null/undefined
          const eligibilityData = await getCourseraPlusUpsellEligibilityData(productItemId, productType);
          setIsEligible(!!eligibilityData);
        } else {
          // For non-Freemium pricing, we use the legacy API that returns a boolean
          const eligible = await isEligibleForCourseraPlusUpgrade(productItemId, productType);
          setIsEligible(eligible);
        }
      } catch {
        setIsEligible(false);
      }
    };

    checkEligibility();
  }, [productItemId, productType, skip]);

  return isEligible;
};

export const generateImpressionForPrePurchaseUpsellExperiment = (): void => {
  // Link to epic: https://tools.coursera.org/epic/experiment/RCl4RbamEfC-lAr_4rnFJw
  courseraPlusExperiments.get('prePurchaseUpsellVariant');
};

export const previewPrePurchaseUpsellVariant = () => {
  return courseraPlusExperiments.preview('prePurchaseUpsellVariant');
};
